/**
 * Mein Tag – Einstiegsseite.
 *
 * Zweispaltig ab 1080 px: links die Arbeit, rechts das Team. Vorher lag alles
 * in einer 720 px schmalen Spalte, während zwei Drittel der Fläche leer
 * blieben und der Team-Überblick unter den Falz rutschte.
 *
 * Die große Zahl ist exakt die Länge der Liste darunter – keine gekürzte
 * Ansicht mit „3 weitere sind fällig" daneben. Was nicht dran ist, steht als
 * ein ruhiger Satz am Fuß der Karte.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { IconInfo, IconTelefon, IconMail } from '../komponenten/Icons';
import { tageSeit, whatsappNummer } from '../hilfen/format';
import { AnrufModal } from '../modals/AnrufModal';
import { ErinnerungModal } from '../modals/ErinnerungModal';
import type { Angebot } from '../typen';
import './meintag.css';


/** Ein Vorgang, der beim Kunden liegt. */
interface Aufgabe {
  angebot: Angebot;
  /** Tage beim Kunden, bzw. Tage seit dem Wiedervorlage-Termin. */
  tage: number;
  /** Was zuletzt geschah – Erinnerung oder Wiedervorlage. */
  art: string;
  /** Wartet auf einen Termin und braucht heute nichts. */
  ruht: boolean;
  nummer: string;
}

export function MeinTag() {
  const { nutzer, modalOeffnen, darf } = useKora();
  const meta = useMeta();
  const navigate = useNavigate();

  /** Vorgang, für den gerade eine Rückmeldung erfasst wird. */
  const [rueckmeldung, setRueckmeldung] = useState<Angebot | null>(null);
  /** Vorgang, für den gerade eine Erinnerung versendet wird. */
  const [mailFuer, setMailFuer] = useState<Angebot | null>(null);

  const angeboteAbfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
    enabled: darf('monitor'),
  });

  const angebote = useMemo(() => angeboteAbfrage.data ?? [], [angeboteAbfrage.data]);
  const meinName = nutzer?.name ?? '';

  /**
   * „Meine" Vorgänge: die, die ich führe – Versender, ersatzweise Kalkulant.
   * Dieselbe Regel wie im Team-Überblick und im Cockpit.
   */
  const meine = useMemo(
    () => angebote.filter((a) => (a.versender || a.kalkulant) === meinName),
    [angebote, meinName],
  );

  /**
   * Was beim Kunden liegt – nach Liegezeit sortiert, das Älteste oben.
   *
   * Früher stand hier nur, was „überfällig" war. Das setzte eine Frist voraus,
   * die es nicht gibt: Ein Angebot wird nicht schlecht, es liegt nur länger.
   * Und zwischen den beiden Schwellen fiel alles in ein Loch – die Erinnerung
   * war fällig, die Automatik lief im Trockenlauf, und niemand sah es.
   *
   * Jetzt steht hier, was tatsächlich draußen ist, mit der Zahl der Tage. Was
   * damit geschieht, entscheidet der Mensch: Mail oder Anruf.
   */
  const aufgaben = useMemo<Aufgabe[]>(() => {
    const liste: Aufgabe[] = [];
    for (const a of meine) {
      if (a.wvZustand && a.wiedervorlage) {
        // Ein pausierter Vorgang wartet auf seinen Termin, nicht auf uns.
        liste.push({
          angebot: a,
          tage: Math.max(0, tageSeit(a.wiedervorlage, meta.heute)),
          art: a.wvZustand === 'vor' ? `Wiedervorlage ${a.wvText}` : a.wvText,
          ruht: a.wvZustand === 'vor',
          nummer: a.name,
        });
      } else if (a.beimKunden) {
        liste.push({
          angebot: a,
          tage: a.tageVersand,
          art: a.erinnerungen.length
            ? `${a.erinnerungen.length}. Erinnerung am ${a.erinnerungen[0]!.datumText}`
            : 'noch keine Erinnerung',
          ruht: false,
          nummer: a.name,
        });
      }
    }
    return liste.sort((x, y) => y.tage - x.tage);
  }, [meine, meta.heute]);

  /*
   * Die Farbbänder kommen zentral vom Server. Es sind Farben, keine Fristen:
   * Sie sagen, wie lange ein Angebot schon liegt, nicht dass es zu lange
   * liegt. Gepflegt werden sie in den Einstellungen.
   */
  const { gruenBis, orangeBis } = meta.liegezeit;
  const lange = aufgaben.filter((x) => !x.ruht && x.tage > orangeBis).length;

  /**
   * Team-Überblick – für alle sichtbar, ohne Rechteprüfung.
   *
   * Jeder soll sehen, wie die Angebote der anderen stehen. Beschränkt bleiben
   * nur Auftragsübersicht und Finanzen, also die Bereiche mit Beträgen.
   *
   * Der eigene Eintrag entfällt: Er wäre dieselbe Aussage wie die Karte links.
   */
  const team = useMemo(() => {
    const offenAlle = angebote.filter((a) => a.offen);
    return meta.team
      .filter((p) => p.imVertrieb && p.name !== meinName)
      .map((p) => {
        const seine = offenAlle.filter((a) => (a.versender || a.kalkulant) === p.name);
        return {
          ...p,
          anzahl: seine.length,
          faellig: seine.filter((a) => a.nachfass.anrufFaellig || a.wvFaellig).length,
        };
      })
      .filter((t) => t.anzahl > 0)
      .sort((x, y) => y.faellig - x.faellig || y.anzahl - x.anzahl);
  }, [angebote, meta.team, meinName]);

  /** Erste erreichbare Nummer des Kunden – der Prozess ist anrufgetrieben. */
  function nummerVon(a: Angebot): string {
    return a.kunde.telefon || a.kunde.mobil || a.kunde.mobilGeschaeftlich || '';
  }

  return (
    <div className="tag">
      <div className="tag-kopf">
        <div>
          <div className="tag-kicker">{meta.heuteText}</div>
          <h1 className="tag-gruss">Willkommen, {nutzer?.name}</h1>
        </div>
        {darf('monitor') && (
          <button type="button" className="tag-ausgang" onClick={() => navigate('/cockpit')}>
            Alle Vorgänge im Vertriebscockpit →
          </button>
        )}
      </div>

      {!darf('monitor') ? (
        <div className="eh-karte">
          Für dich ist der Vertriebsbereich nicht freigeschaltet. Die Freigabe erteilt die
          Administration im Admin Center.
        </div>
      ) : (
        <div className="tag-raster">
          <div className="tag-spalte">
            {/* --- Auf deinem Tisch --- */}
            <div className="tag-tisch">
              <div className="tag-tisch-kopf">
                <div className="tag-label">Auf deinem Tisch</div>
                {lange > 0 && (
                  <span className="tag-eskalation">
                    <span className="tag-eskalation-punkt" />
                    {lange} länger als {orangeBis} Tage
                  </span>
                )}
              </div>

              <div className="tag-zahl-zeile">
                <span className={`tag-zahl ${aufgaben.length ? '' : 'ruhig'}`}>
                  {aufgaben.length}
                </span>
                <span className="tag-zahl-text">
                  {aufgaben.length === 1 ? 'Angebot liegt beim Kunden' : 'Angebote liegen beim Kunden'}
                </span>
              </div>

              <div className="tag-liste">
                {aufgaben.map((x) => (
                  <div
                    key={x.angebot.id}
                    className="tag-zeile"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/angebot/${x.angebot.id}`, { state: { herkunft: 'meintag' } })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        navigate(`/angebot/${x.angebot.id}`, { state: { herkunft: 'meintag' } });
                      }
                    }}
                  >
                    <div className="tag-zeile-links">
                      <div className={`tag-nummer ${!x.ruht && x.tage > orangeBis ? 'dringend' : ''}`}>
                        {x.nummer}
                      </div>
                      <div className="tag-kunde">
                        {x.angebot.kunde.ort} · {x.angebot.kunde.name}
                      </div>
                    </div>

                    <div className="tag-zeile-mitte">
                      <div className={`tag-tage ${x.ruht ? 'ruht' : stufe(x.tage, gruenBis, orangeBis)}`}>
                        {x.ruht ? x.art : tageText(x.tage)}
                      </div>
                      <div className="tag-art">{x.ruht ? 'wartet auf den Termin' : x.art}</div>
                    </div>

                    {/*
                      Handeln ohne Umweg. Der Prozess ist anrufgetrieben – wer
                      für jeden Anruf erst das Angebot öffnen muss, arbeitet die
                      Liste nicht ab. Die Knöpfe stoppen das Klick-Ereignis,
                      sonst öffnete sich zusätzlich die Detailmaske.
                    */}
                    <div className="tag-aktionen">
                      <a
                        className={`tag-aktion ${nummerVon(x.angebot) ? '' : 'ruht'}`}
                        href={nummerVon(x.angebot) ? `tel:${whatsappNummer(nummerVon(x.angebot))}` : undefined}
                        title={
                          nummerVon(x.angebot)
                            ? `${nummerVon(x.angebot)} anrufen`
                            : 'Keine Telefonnummer hinterlegt'
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconTelefon groesse={15} />
                      </a>
                      {/*
                        Die Mail geht nicht von allein hinaus. Wer den Vorgang
                        führt, weiß oft mehr als Kora – ein Gespräch von
                        gestern, eine Zusage. Deshalb Vorschau, Text ändern
                        können, dann senden.
                      */}
                      <button
                        type="button"
                        className={`tag-aktion ${x.angebot.kunde.mail ? '' : 'ruht'}`}
                        title={
                          x.angebot.kunde.mail
                            ? 'Erinnerung ansehen und versenden'
                            : 'Keine E-Mail-Adresse hinterlegt'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          if (x.angebot.kunde.mail) setMailFuer(x.angebot);
                        }}
                      >
                        <IconMail groesse={15} />
                      </button>

                      <button
                        type="button"
                        className="tag-aktion"
                        title="Rückmeldung erfassen"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRueckmeldung(x.angebot);
                        }}
                      >
                        <IconSprechblase groesse={15} />
                      </button>
                    </div>
                  </div>
                ))}

                {aufgaben.length === 0 && (
                  <div className="tag-leer">
                    Heute ist nichts fällig. Vorgänge erscheinen hier automatisch, sobald ein Anruf
                    oder eine Wiedervorlage ansteht.
                  </div>
                )}
              </div>

            </div>

          </div>

          {/* --- Rechte Spalte --- */}
          <div className="tag-seitenspalte">
            <div className="tag-karte">
              <div className="tag-label">Team-Überblick</div>
              <div className="tag-team">
                {team.map((p) => (
                  <div className="tag-person" key={p.name}>
                    <span className="tag-avatar">{p.initialen}</span>
                    <span className="tag-person-text">
                      <span className="tag-person-name">{p.name}</span>
                      <span className="tag-person-meta">
                        {p.anzahl} {p.anzahl === 1 ? 'Angebot' : 'Angebote'} beim Kunden
                      </span>
                    </span>
                    <span className={`tag-person-faellig ${p.faellig ? 'offen' : ''}`}>
                      {p.faellig === 0
                        ? 'nichts fällig'
                        : p.faellig === 1
                          ? '1 Handlung fällig'
                          : `${p.faellig} Handlungen fällig`}
                    </span>
                  </div>
                ))}

                {team.length === 0 && (
                  <div className="tag-leer">Bei den anderen liegt gerade kein Angebot.</div>
                )}
              </div>

              <div className="tag-team-hinweis">
                Deine eigenen Vorgänge stehen links unter „Auf deinem Tisch" – hier stehen nur die
                anderen.
              </div>
            </div>

            <div className="tag-fuss">
              <span>
                Kora {meta.version}
                {meta.trockenlauf ? ' · Testbetrieb, Kundenmails werden nicht versendet' : ''}
              </span>
              <button
                type="button"
                className="tag-info"
                title="Was ist neu?"
                onClick={() => modalOeffnen({ art: 'changelog', alle: true })}
              >
                <IconInfo />
              </button>
            </div>
          </div>
        </div>
      )}

      {rueckmeldung && (
        <AnrufModal
          angebot={rueckmeldung}
          onSchliessen={() => setRueckmeldung(null)}
          onFertig={() => angeboteAbfrage.refetch()}
        />
      )}

      {mailFuer && (
        <ErinnerungModal
          angebot={mailFuer}
          onSchliessen={() => setMailFuer(null)}
          onFertig={() => angeboteAbfrage.refetch()}
        />
      )}

    </div>
  );
}

/**
 * Farbband der Liegezeit.
 *
 * Drei Bänder, zentral eingestellt: bis `gruenBis` grün, bis `orangeBis`
 * orange, darüber rot. Die Farbe sagt, wie lange etwas liegt – nicht, dass
 * jemand etwas versäumt hätte. Es gibt keine Frist.
 */
function stufe(tage: number, gruenBis: number, orangeBis: number): string {
  if (tage > orangeBis) return 'rot';
  if (tage > gruenBis) return 'orange';
  return 'gruen';
}

function tageText(tage: number): string {
  if (tage <= 0) return 'heute versendet';
  return `beim Kunden seit ${tage} ${tage === 1 ? 'Tag' : 'Tagen'}`;
}

/** Sprechblase – steht für „Rückmeldung erfassen". */
function IconSprechblase({ groesse = 15 }: { groesse?: number }) {
  return (
    <svg
      width={groesse}
      height={groesse}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <path d="M20 12a7.5 7.5 0 0 1-7.5 7.5H8l-4 3v-4.4A7.5 7.5 0 0 1 12.5 4.5 7.5 7.5 0 0 1 20 12Z" />
    </svg>
  );
}